import {
  Component,
  computed,
  Signal,
  viewChild,
  ViewEncapsulation,
  effect,
  signal,
  WritableSignal,
  inject,
} from '@angular/core';
import {CartItemsListComponent} from './cart-items-list/cart-items-list.component';
import {DunningInfoComponent} from './dunning-info/dunning-info.component';
import {InvoiceListComponent} from './invoice-list/invoice-list.component';
import {ItemCorrectionComponent} from "./item-correction-list/item-correction.component";
import {Splitter, SplitterModule} from "primeng/splitter";
import {filter, tap} from "rxjs/operators";
import {ControlStore, EControlActions, IControlDataChanged} from "frontier";
import AppControlGuids from "../../../../../api/ControlGuids";
import {Subscription} from "rxjs";

@Component({
  selector: 'kpi4me-invoice-detail',
  templateUrl: './invoice-detail.component.html',
  styleUrls: ['./invoice-detail.component.scss'],
  encapsulation: ViewEncapsulation.Emulated,
  standalone: true,
  imports: [
    InvoiceListComponent,
    DunningInfoComponent,
    CartItemsListComponent,
    ItemCorrectionComponent,
    SplitterModule,
  ],
})
export class InvoiceDetailComponent {
  protected readonly controlStore: ControlStore = inject(ControlStore);

  protected subs: Subscription = new Subscription();

  dunningRef: Signal<DunningInfoComponent> = viewChild(DunningInfoComponent);
  itemCorrectionRef: Signal<ItemCorrectionComponent> = viewChild(ItemCorrectionComponent);

  splitterComponent: Signal<Splitter> = viewChild(Splitter);

  newDunning: WritableSignal<boolean> = signal(false);
  newItemCorrection: WritableSignal<boolean> = signal(false);

  private _initialPanels: any[] | undefined;

  initialPanels: Signal<any[]> = computed((): any[] => {
    if (!this._initialPanels) {
      this._initialPanels = this.splitterComponent()?.panels;
    }
    return this._initialPanels;
  })

  isDunningInfoShown: Signal<boolean> = computed((): boolean => {
    if (this.dunningRef()?.apiData() === null) return true;
    return this.dunningRef()?.rowCountSignal() > 0;
  });

  isItemCorrectionShown: Signal<boolean> = computed((): boolean => {
    if (this.itemCorrectionRef()?.apiData() === null) return true;
    return this.itemCorrectionRef()?.rowCountSignal() > 0;
  });

  constructor() {
    effect((): void => {
      const isDunningInfoShown: boolean = this.isDunningInfoShown() || this.newDunning();
      const isItemCorrectionShown: boolean = this.isItemCorrectionShown() || this.newItemCorrection();
      const initialPanels: any[] = this.initialPanels();
      let panels;
      if (isDunningInfoShown && isItemCorrectionShown) {
        panels = initialPanels;
      } else if (!isDunningInfoShown && isItemCorrectionShown) {
        panels = [initialPanels[0], initialPanels[2], initialPanels[3]];
      } else if (isDunningInfoShown && !isItemCorrectionShown) {
        panels = [initialPanels[0], initialPanels[1], initialPanels[2]];
      } else {
        panels = [initialPanels[0], initialPanels[2]];
      }
      this.splitterComponent().panels = panels;
    });

    // After the ItemCorrectionTable/DunningInfoTable has been loaded, reset the newItemCorrection/newDunning flag to
    // avoid preventing removal of the said table if its last row gets removed.
    effect((): void => {
      const isItemCorrectionShown: boolean = this.isItemCorrectionShown()
      if (isItemCorrectionShown) {
        this.newItemCorrection.set(false);
      }
    }, {allowSignalWrites: true});

    effect((): void => {
      const isDunningInfoShown: boolean = this.isDunningInfoShown()
      if (isDunningInfoShown) {
        this.newDunning.set(false);
      }
    }, {allowSignalWrites: true});

    // Trigger initialization of itemCorrectionTable/dunningInfoTable when actions are triggered that usually generate a
    // row in the said table.
    this.subs.add(
      this.controlStore.controlDataChanged$.pipe(
        filter((evt: IControlDataChanged): boolean => {
          return new Map([
            [AppControlGuids.TCartItemsList, new Set([
              EControlActions.partlyCancel,
              EControlActions.cancelItemValue,
              EControlActions.invoice,
            ])],
            [AppControlGuids.TInvoiceList, new Set([EControlActions.cancel])],
            [AppControlGuids.TCartList, new Set([EControlActions.detail])],
          ]).get(evt.GUID)?.has(evt.changeType);
        }),
        tap((): void => {
          if(!this.isItemCorrectionShown()) this.newItemCorrection.set(true);
        })
      ).subscribe(),
    );

    this.subs.add(
      this.controlStore.controlDataChanged$.pipe(
        filter((evt: IControlDataChanged): boolean => {
          return new Map([
            [AppControlGuids.TCartList, new Set([EControlActions.detail, EControlActions.dunning])],
          ]).get(evt.GUID)?.has(evt.changeType);
        }),
        tap((): void => {
          if(!this.isDunningInfoShown) this.newDunning.set(true);
        })
      ).subscribe(),
    );
  }
}
